import { cn } from '@/lib/utils';
import { useCartStore, CartItem as CartItemType } from '@/store/cart';
import { useRealtimeStock } from '@/hooks/useRealtimeStock';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';

interface CartItemProps {
  item: CartItemType;
}

export function CartItem({ item }: CartItemProps) {
  const { updateQuantity, removeItem, orderType } = useCartStore();
  const [isLowStock, setIsLowStock] = useState(false);
  const [currentStock, setCurrentStock] = useState(item.currentStock);

  // Real-time stock monitoring - 예약할인은 제외
  const shouldMonitorStock = orderType !== 'RESERVATION_DISCOUNT';
  
  if (shouldMonitorStock) {
    useRealtimeStock(
      [item.productId],
      {
        onStockUpdate: (productId, newStock) => {
          setCurrentStock(newStock);
          setIsLowStock(newStock < item.quantity);
          
          // Auto-adjust quantity if stock is lower
          if (newStock < item.quantity && newStock > 0) {
            updateQuantity(productId, newStock, user?.id);
          }
        },
        onOutOfStock: () => {
          setCurrentStock(0);
          setIsLowStock(true);
        },
        onBackInStock: () => {
          // 재입고 처리는 onStockUpdate에서 처리됨
          setIsLowStock(false);
        }
      }
    );
  }

  // Check if current quantity exceeds stock
  useEffect(() => {
    setIsLowStock(currentStock < item.quantity);
  }, [currentStock, item.quantity]);

  const { user } = useAuth();

  const handleQuantityChange = async (delta: number) => {
    const newQuantity = item.quantity + delta;
    const maxQuantity = orderType === 'RESERVATION_DISCOUNT' ? 999 : currentStock;
    if (newQuantity >= 1 && newQuantity <= maxQuantity) {
      await updateQuantity(item.productId, newQuantity, user?.id);
    }
  };

  const handleRemove = async () => {
    await removeItem(item.productId, user?.id);
  };

  return (
    <div
      className={cn(
        "flex gap-3 p-2 rounded-lg border-2 transition-colors",
        isLowStock ? "border-[#FF3B30]" : "border-[#DEDEDE]"
      )}
    >
      {/* Product Image */}
      <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
        {item.productImage ? (
          <img
            src={item.productImage}
            alt={item.productName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M3 9L12 3L21 9" stroke="currentColor" strokeWidth="2"/>
              <circle cx="12" cy="13" r="3" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-medium text-[#222222] pr-2 truncate">
            {item.productName}
          </h3>
          <button
            onClick={handleRemove}
            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M6 6L14 14M14 6L6 14" stroke="#222222" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Options if any */}
        {item.options && (
          <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
            <span>옵션:</span>
            <span>{item.options}</span>
          </div>
        )}

        {/* Stock Status - 예약할인은 예약 상품, 나머지는 재고 표시 */}
        {orderType === 'RESERVATION_DISCOUNT' ? (
          <div className="flex items-center gap-1 text-sm mb-3">
            <span className="text-green-600 font-medium">예약 상품</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-sm mb-3">
            <span className="text-gray-600">현재 재고:</span>
            <span className={cn(
              "font-medium",
              currentStock === 0 && "text-[#FF3B30] font-bold",
              currentStock > 0 && currentStock < 5 && "text-[#FF3B30]",
              currentStock >= 5 && "text-[#222222]"
            )}>
              {currentStock === 0 ? '품절' : `${currentStock}개`}
            </span>
          </div>
        )}

        {/* Quantity Controls & Price */}
        <div className="flex items-center justify-between">
          <div className="flex items-center border border-gray-300 rounded-lg">
            <button
              onClick={() => handleQuantityChange(-1)}
              disabled={item.quantity <= 1}
              className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M5 10H15" stroke="#222222" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            <span className="px-3 py-1 min-w-[40px] text-center font-medium">
              {item.quantity}
            </span>
            <button
              onClick={() => handleQuantityChange(1)}
              disabled={orderType !== 'RESERVATION_DISCOUNT' && item.quantity >= currentStock}
              className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M10 5V15M5 10H15" stroke="#222222" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="flex items-baseline gap-1">
            <span className="text-base font-medium text-[#222222]">
              {(item.discountedPrice * item.quantity).toLocaleString()}
            </span>
            <span className="text-sm text-[#222222]">원</span>
          </div>
        </div>
      </div>
    </div>
  );
}